import React, { useId, useState } from 'react';
import { Field } from './Modal';
import { useConfirm } from './ConfirmDialog';
import { getInvoiceDefaults } from '../utils/billingStore';
import { formatGBP, fromPence, sumPence } from '../utils/money';

/**
 * <QuoteSections /> — the narrative body of a quote.
 *
 * Real quotations aren't price lists: Scope of Works, Programme, Site Requirements,
 * Deliverables, Exclusions, Commercial Basis — each a heading with paragraphs,
 * bullets, or a costed breakdown — and the price arrives at the end. Each section is
 * { title, body, bullets[] } or { title, kind:'table', body, rows[{label, amount}] }
 * and prints in order ahead of the costs. Presets come from Settings → Quote terms.
 *
 * @param {object[]} sections
 * @param {(next)=>void} onChange
 * @param {string} subtitle            "Quotation for …" line
 * @param {(v)=>void} onSubtitleChange
 * @param {boolean} [disabled]         read-only (issued quote)
 */

const presets = () => {
  const p = getInvoiceDefaults().quoteSectionPresets;
  return Array.isArray(p) ? p : [];
};

const NEW_SECTION = (kind) => (kind === 'table'
  ? { title: '', kind: 'table', body: '', rows: [{ label: '', amount: '' }] }
  : { title: '', body: '', bullets: [] });

const hasContent = (s) => !!(String(s.title || '').trim() || String(s.body || '').trim()
  || (s.bullets || []).some((b) => String(b).trim()) || (s.rows || []).some((r) => String(r.label || '').trim() || String(r.amount ?? '').trim()));

const area = { minHeight: 68, resize: 'vertical', lineHeight: 1.5 };
const iconBtn = { minWidth: 36, minHeight: 36, padding: '0.3rem 0.5rem' };

const QuoteSections = ({ sections = [], onChange, subtitle = '', onSubtitleChange, disabled = false }) => {
  const [addOpen, setAddOpen] = useState(false);
  const { confirm } = useConfirm();
  const base = useId().replace(/:/g, '');

  const patch = (i, key, value) => onChange(sections.map((s, n) => (n === i ? { ...s, [key]: value } : s)));
  const remove = async (i) => {
    const s = sections[i];
    if (hasContent(s)) {
      const ok = await confirm({
        title: `Remove “${String(s.title || '').trim() || `section ${i + 1}`}”?`,
        body: 'Its text will be taken off this quote.',
        confirmLabel: 'Remove section',
        tone: 'danger',
      });
      if (!ok) return;
    }
    onChange(sections.filter((_, n) => n !== i));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (preset) => {
    const next = preset
      ? { ...preset, bullets: [...(preset.bullets || [])], rows: preset.rows ? preset.rows.map((r) => ({ ...r })) : undefined }
      : NEW_SECTION();
    onChange([...sections, next]);
    setAddOpen(false);
  };
  // Bullets are edited one per line, which is how people actually type a list.
  const bulletsText = (s) => (s.bullets || []).join('\n');
  const setBullets = (i, text) => patch(i, 'bullets', text.split('\n'));

  const setRow = (i, ri, key, value) => patch(i, 'rows', (sections[i].rows || []).map((r, n) => (n === ri ? { ...r, [key]: value } : r)));
  const addRow = (i) => patch(i, 'rows', [...(sections[i].rows || []), { label: '', amount: '' }]);
  const removeRow = (i, ri) => patch(i, 'rows', (sections[i].rows || []).filter((_, n) => n !== ri));
  const tableTotal = (s) => formatGBP(fromPence(sumPence(s.rows || [], (r) => r.amount)));

  const used = new Set(sections.map((s) => String(s.title || '').trim().toLowerCase()));
  const PRESET_LIST = presets();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Field label="Quotation for" hint="Prints under the title — “Quotation – External Glass and Lamination Inspection”.">
        <input value={subtitle} onChange={(e) => onSubtitleChange(e.target.value)} disabled={disabled}
          placeholder="e.g. External Glass and Lamination Inspection" />
      </Field>

      {sections.length === 0 && (
        <div className="cw-empty compact" style={{ border: '1px dashed var(--cw-border-strong)', borderRadius: 'var(--cs-radius-md, 10px)', background: 'var(--cw-surface-alt)' }}>
          <p className="cw-empty-title">No sections yet</p>
          <p className="cw-empty-body">Add Scope of Works, Programme, Exclusions and the rest — they print above the costs.</p>
        </div>
      )}

      {sections.map((s, i) => {
        const label = String(s.title || '').trim() || `Section ${i + 1}`;
        const hid = `${base}-s${i}`;
        return (
          <section key={i} aria-labelledby={`${hid}-h`} style={{ border: '1px solid var(--cw-border)', borderRadius: 'var(--cs-radius-md, 10px)', background: 'var(--cw-surface)', padding: 11 }}>
            <h4 id={`${hid}-h`} className="cw-sr-only">{label}</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span aria-hidden="true" className="cw-meta" style={{ fontWeight: 700, minWidth: 18 }}>{i + 1}</span>
              <input className="cw-input" style={{ fontWeight: 700, flex: '1 1 180px', width: 'auto' }} value={s.title || ''}
                aria-label={`Section ${i + 1} heading`} disabled={disabled}
                onChange={(e) => patch(i, 'title', e.target.value)} placeholder="Section heading" />
              {!disabled && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" className="cw-btn cw-btn-secondary" style={iconBtn}
                    aria-label={`Move ${label} up`} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><i className="fa-solid fa-arrow-up" aria-hidden="true" /></button>
                  <button type="button" className="cw-btn cw-btn-secondary" style={iconBtn}
                    aria-label={`Move ${label} down`} title="Move down" disabled={i === sections.length - 1} onClick={() => move(i, 1)}><i className="fa-solid fa-arrow-down" aria-hidden="true" /></button>
                  <button type="button" className="cw-btn cw-btn-danger-outline" style={iconBtn}
                    aria-label={`Remove ${label}`} title="Remove section" onClick={() => remove(i)}><i className="fa-solid fa-trash" aria-hidden="true" /></button>
                </div>
              )}
            </div>
            <textarea className="cw-input" style={area} value={s.body || ''} disabled={disabled}
              aria-label={`${label} — paragraph text`}
              onChange={(e) => patch(i, 'body', e.target.value)}
              placeholder="Paragraph text — leave blank if this section is just a list." />

            {s.kind === 'table' ? (
              <div style={{ marginTop: 8 }}>
                <p className="cw-label" style={{ margin: '0 0 4px' }}>
                  Costed breakdown <span className="cw-meta" style={{ fontWeight: 400 }}>— shown in the body, separate from the priced items below</span>
                </p>
                {(s.rows || []).map((r, ri) => (
                  <div key={ri} style={{ display: 'flex', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                    <input className="cw-input" style={{ flex: '3 1 200px', width: 'auto' }} value={r.label || ''} disabled={disabled}
                      aria-label={`${label} row ${ri + 1} item`}
                      placeholder="e.g. Design, manufacture and supply of replacement BMU"
                      onChange={(e) => setRow(i, ri, 'label', e.target.value)} />
                    <input className="cw-input" style={{ flex: '1 1 110px', width: 'auto' }} value={r.amount ?? ''} disabled={disabled}
                      aria-label={`${label} row ${ri + 1} amount in pounds`} inputMode="decimal"
                      placeholder="175,000"
                      onChange={(e) => setRow(i, ri, 'amount', e.target.value)} />
                    {!disabled && (
                      <button type="button" className="cw-btn cw-btn-danger-outline" style={iconBtn}
                        aria-label={`Remove ${label} row ${ri + 1}`} title="Remove row" onClick={() => removeRow(i, ri)}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                  {!disabled && (
                    <button type="button" className="cw-btn cw-btn-secondary" onClick={() => addRow(i)}>
                      <i className="fa-solid fa-plus" aria-hidden="true" /> Add row
                    </button>
                  )}
                  <span style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text-2)' }}>
                    Breakdown total <strong>{tableTotal(s)}</strong>
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 7 }}>
                <label className="cw-label" htmlFor={`${hid}-b`} style={{ display: 'block', marginBottom: 3 }}>
                  Bullet points <span className="cw-meta" style={{ fontWeight: 400 }}>— one per line</span>
                </label>
                <textarea id={`${hid}-b`} className="cw-input" style={{ ...area, minHeight: 56 }} value={bulletsText(s)} disabled={disabled}
                  onChange={(e) => setBullets(i, e.target.value)}
                  placeholder={'Visual inspection of the external glass panels.\nPhotographic recording of identified defects.'} />
              </div>
            )}
          </section>
        );
      })}

      {!disabled && (
        <div>
          {!addOpen ? (
            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setAddOpen(true)} aria-expanded="false">
              <i className="fa-solid fa-plus" aria-hidden="true" /> Add section
            </button>
          ) : (
            <div role="group" aria-labelledby={`${base}-add`} style={{ border: '1px solid var(--cw-border)', background: 'var(--cw-blue-tint)', borderRadius: 'var(--cs-radius-md, 10px)', padding: 11 }}>
              <p id={`${base}-add`} className="cw-label" style={{ margin: '0 0 7px' }}>Add a section</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PRESET_LIST.map((pr) => {
                  const already = used.has(String(pr.title || '').toLowerCase());
                  return (
                    <button type="button" key={pr.title} className="cw-btn cw-btn-secondary"
                      disabled={already}
                      title={already ? 'Already on this quote' : `Insert a ${pr.title} section`}
                      onClick={() => add(pr)}>
                      {pr.kind === 'table' && <i className="fa-solid fa-table-list" aria-hidden="true" style={{ marginRight: 5 }} />}
                      {pr.title}{already && <span className="cw-sr-only"> (already added)</span>}
                    </button>
                  );
                })}
                <button type="button" className="cw-btn cw-btn-primary" onClick={() => add(null)}>
                  <i className="fa-solid fa-plus" aria-hidden="true" /> Blank section
                </button>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => add(NEW_SECTION('table'))}>
                  <i className="fa-solid fa-table-list" aria-hidden="true" /> Blank table
                </button>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setAddOpen(false)}>Cancel</button>
              </div>
              <p className="cw-field-hint" style={{ marginTop: 7 }}>Presets come pre-filled with typical wording — edit freely once added.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteSections;
